import { Button } from '@material-ui/core';
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import { useContext, useEffect, useState } from 'react';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { DropzoneFilesIcon } from 'src/components/icons';
import { GetErrorCodeMessage } from 'src/constants/errorConsts/errorCodeMessages';
import { RouteContext } from 'src/context';
import { ContractBuilderContext } from 'src/context/contractBuilderContext';
import { useProgressUpload } from 'src/hooks/useProgressUpload';
import { useAppDispatch } from 'src/hooks/useStore';
import { useCreateContractFilesMutation } from 'src/services/api/contractsApi';
import {
  requestLoadPages,
  ResetESignToInitialValues,
} from 'src/store/signaturePage/actions';
import { alertSnackbar } from 'src/store/ui/actions';
import { GraySmall } from 'src/theme/colors';
import { FileWithPreview } from 'src/types/files';
import { S3Utils } from 'src/utils';
import { ensureApiError } from 'src/utils/Errors';
import FileUtils from 'src/utils/FileUtils';
import { beautifyFileName } from 'src/utils/StringUtils';

import { FileDropzone } from '../FileDropzone';
import { getFileNameFromFileKey } from '../Files/helpers';
import MemoBaseTypography from '../Text/BaseTypography';
import { FileInputUploadItem } from '../UI/FileUploadInputItem';

import { ContractTemplateForm } from './ContractBuilderAndSignerForm';
import { ContractDetailsPageQueryParams } from './ContractDetailsPage';

interface ContractFileDropzoneProps {
  name: keyof ContractTemplateForm;
  onRemoveUpload: () => void;
}

/**
 * Drag-and-drop input for uploading contract template PDFs.
 * @param {string} name - The name of the formik field associated with this input.
 *
 * @returns {JSX.Element} The JSX element rendering the contract template PDF upload input.
 */
export const ContractFileDropzone = ({
  name,
  onRemoveUpload,
}: ContractFileDropzoneProps) => {
  const { query } = useContext(RouteContext);
  const { templateId } = query as ContractDetailsPageQueryParams;
  const isEditTemplateFlow = Boolean(templateId);
  const dispatch = useAppDispatch();
  const { contractPdfInfo, setContractPdfInfo } = useContext(
    ContractBuilderContext,
  );
  const { setFieldValue, setValues, values } =
    useFormikContext<ContractTemplateForm>();

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [createContractFile] = useCreateContractFilesMutation();

  const { startUploadFiles, setUploadProgress, exitUploadFiles } =
    useProgressUpload();

  const [selectedPdfFile, setSelectedPdfFile] =
    useState<FileWithPreview | null>(null);

  // State to track the file size of the S3-uploaded file.
  // This value will be used to display the file size of a PDF during the edit flow.
  // For the create flow, the file size can be obtained from the local file.
  const [s3UploadedFileSize, setS3UploadedFileSize] = useState(0);

  /**
   * Handles the event when one or more files are dropped into the dropzone.
   *
   * @param {FileWithPreview[]} acceptedFiles - An array of accepted files.
   */
  const handleFileDrop = async (acceptedFiles: FileWithPreview[]) => {
    setErrorMessage(''); // clear old error

    // If no accepted files, exit early.
    if (!acceptedFiles.length) {
      setErrorMessage('Contract template must be a PDF.');
      return;
    }

    const file = acceptedFiles.at(0);

    if (!file) return;

    const filePaths = await S3Utils.uploadFilesToS3({
      acceptedFiles,
      dispatch,
      targetPath: '',
      startUploadFiles,
      setUploadProgress,
      exitUploadFiles,
      ignoreFolders: false,
    });

    const filePath = filePaths.at(0);
    if (!filePath || !filePath.key) {
      setErrorMessage('File upload failed. Please try again.');
      return;
    }
    dispatch(requestLoadPages());
    const response = await createContractFile(filePath.key);
    if ('error' in response) {
      const error = ensureApiError(response.error);
      const errorCode = error._unsupported_code ?? error.code;
      const mappedErrorMessage = GetErrorCodeMessage(errorCode);
      dispatch(
        alertSnackbar({
          errorMessage:
            mappedErrorMessage ||
            (error.code === 'unsupported_media_type'
              ? error.message
              : 'Failed to upload the contract file'),
        }),
      );
      return;
    }

    if (response.data?.warnings && response.data.warnings.length > 0) {
      setErrorMessage(response.data.warnings.at(0) || '');
    }

    setFieldValue(name, file.name);
    setSelectedPdfFile(file);

    // either the .pdf extension is present, or it is not,
    // and in both cases, we want the first element of the split array
    setValues({
      ...values,
      title: values.title?.trim() || beautifyFileName(file.name),
      fileKey: filePath.key,
    });

    setContractPdfInfo({
      fileKey: filePath.key,
      pageFileKeys: response.data.pageKeys,
      identityId: response.data.identityId,
    });
  };

  /**
   * Clears the selected PDF file and resets the form field value.
   */
  const handleRemoveFile = () => {
    setFieldValue(name, '');
    setErrorMessage('');
    setSelectedPdfFile(null);
    dispatch(ResetESignToInitialValues());
    onRemoveUpload();
  };

  /**
   * Handles downloading the contract PDF file.
   */
  const handleDownload = async () => {
    try {
      if (!contractPdfInfo.fileKey || !contractPdfInfo.identityId) {
        console.error('Missing file key or identity ID for download');
        return;
      }

      const s3Url = await S3Utils.getFile(contractPdfInfo.fileKey, {
        identityId: contractPdfInfo.identityId,
        level: 'protected',
      });

      await FileUtils.downloadFileFromUrl(
        s3Url,
        contractPdfInfo.fileKey,
        getFileNameFromFileKey(contractPdfInfo.fileKey),
      );
    } catch (error) {
      console.error(
        'Error occurred while downloading the contract file',
        error,
      );
      // You could also add a user notification here if needed
      dispatch(
        alertSnackbar({ errorMessage: 'Failed to download the contract file' }),
      );
    }
  };

  const hasError = Boolean(errorMessage);

  const getUploadedFileInfo = async () => {
    const listOfFiles = await S3Utils.listFiles(contractPdfInfo.fileKey, {
      identityId: contractPdfInfo.identityId,
      level: 'protected',
    });

    const files = listOfFiles.results;

    // Retrieve the information of the first file in the list (assuming it's a PDF).
    const pdfFile = files.at(0);
    if (pdfFile) {
      setS3UploadedFileSize(pdfFile.size || 0);
    }
  };

  /**
   * Fetch uploaded file information when in edit template flow.
   */
  useEffect(() => {
    if (!isEditTemplateFlow) return;
    if (!contractPdfInfo.fileKey) return;
    getUploadedFileInfo();
  }, [isEditTemplateFlow, contractPdfInfo]);

  if (selectedPdfFile || isEditTemplateFlow) {
    return (
      <>
        <FileInputUploadItem
          className="shadow-none max-w-none"
          containerClassName={clsx({
            'border-error-primary border-dashed': hasError,
          })}
          name={
            selectedPdfFile?.name ||
            getFileNameFromFileKey(contractPdfInfo.fileKey)
          }
          size={selectedPdfFile?.size || s3UploadedFileSize}
          extension="pdf"
          readOnly={isEditTemplateFlow}
          onRemove={handleRemoveFile}
          onDownload={handleDownload}
        />
        {errorMessage && (
          <ErrorMessage
            message={errorMessage}
            className="text-right text-sm -mt-6"
          />
        )}
      </>
    );
  }

  return (
    <div>
      <MemoBaseTypography fontType="13Medium">
        Upload a .pdf file
      </MemoBaseTypography>
      <FileDropzone
        accept={['application/pdf']}
        onDrop={handleFileDrop}
        disabled={isEditTemplateFlow}
      >
        <div
          className={clsx(
            'flex flex-col justify-center items-center border border-dashed gap-4 rounded p-[26px]',
            {
              'border-error-primary': hasError,
              'border-[#ccc]': !hasError,
            },
          )}
        >
          <DropzoneFilesIcon
            style={{
              fontSize: 60,
            }}
          />
          <Button variant="contained" color="secondary" size="medium">
            Choose a file
          </Button>

          <MemoBaseTypography
            fontType="12Medium"
            style={{
              color: GraySmall,
            }}
          >
            Or drop a file to upload
          </MemoBaseTypography>
        </div>
      </FileDropzone>
      {hasError && (
        <ErrorMessage message={errorMessage} className="text-right text-sm" />
      )}
    </div>
  );
};
